/** Version one-shot épic 4 — incrémenter le suffixe pour une nouvelle campagne. */
export const WHATS_NEW_EPIC4_VERSION = '2026-10-epic4';

export const WHATS_NEW_EPIC4_ARTICLE_URL = 'https://whats-new-rintintin.netlify.app/3';

export const WHATS_NEW_EPIC4_SLIDE_IDS = [
  'intro',
  'booking',
  'followUp',
  'trust',
  'roadmap',
] as const;

export type WhatsNewEpic4SlideId = (typeof WHATS_NEW_EPIC4_SLIDE_IDS)[number];

export const WHATS_NEW_EPIC4_ITEM_IDS = ['booking', 'followUp', 'trust'] as const;

export type WhatsNewEpic4ItemId = (typeof WHATS_NEW_EPIC4_ITEM_IDS)[number];

const BLOCKED_PATH_FRAGMENTS = [
  '/demarrage',
  '/welcome',
  '/subscription',
  '/offre-configuration',
  '/welcome-pricing',
] as const;

export function whatsNewEpic4StorageKey(proId: number): string {
  return `rtt_whats_new_seen_${WHATS_NEW_EPIC4_VERSION}_${proId}`;
}

export function hasSeenWhatsNewEpic4(proId: number | null | undefined): boolean {
  if (!proId) return true;
  try {
    return localStorage.getItem(whatsNewEpic4StorageKey(proId)) === '1';
  } catch {
    return true;
  }
}

export function markWhatsNewEpic4Seen(proId: number): void {
  try {
    localStorage.setItem(whatsNewEpic4StorageKey(proId), '1');
  } catch {
    // quota / mode privé
  }
}

export function isWhatsNewEpic4BlockedPath(pathname: string): boolean {
  const path = (pathname || '').toLowerCase();
  return BLOCKED_PATH_FRAGMENTS.some((fragment) => path.includes(fragment));
}

/** Espace connecté BO : routes sous /app (PrivateLayout). */
export function isWhatsNewEpic4ConnectedPath(pathname: string): boolean {
  const path = (pathname || '').split('?')[0] ?? '';
  return path === '/app' || path.startsWith('/app/');
}

function isElementVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (style.opacity !== '' && Number(style.opacity) === 0) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) return true;
  // jsdom : éléments injectés sans layout explicite
  return el.isConnected && style.display !== 'none';
}

function isWithinExcludedHost(el: HTMLElement, excludeRoot?: HTMLElement | null): boolean {
  if (excludeRoot && (el === excludeRoot || excludeRoot.contains(el))) return true;
  if (el.closest('.whats-new-epic4-host')) return true;
  return false;
}

/**
 * Fenêtres modales visibles (hors pop-up Whats New elle-même).
 * Inclut role=dialog avec ou sans aria-modal, Ant Design, MUI, design-system.
 */
export function countOtherBlockingModals(excludeRoot?: HTMLElement | null): number {
  if (typeof document === 'undefined') return 0;

  const candidates: HTMLElement[] = [];

  const consider = (el: Element | null) => {
    if (!(el instanceof HTMLElement)) return;
    if (isWithinExcludedHost(el, excludeRoot)) return;
    if (!isElementVisible(el)) return;
    candidates.push(el);
  };

  document.querySelectorAll('[role="dialog"]').forEach(consider);

  document.querySelectorAll('.ant-modal-wrap').forEach((wrap) => {
    if (wrap.classList.contains('ant-modal-wrap-hidden')) return;
    consider(wrap);
  });

  document.querySelectorAll('.ant-drawer-open').forEach(consider);

  document.querySelectorAll('.MuiModal-root').forEach((root) => {
    if (root.getAttribute('aria-hidden') === 'true') return;
    consider(root);
  });

  document.querySelectorAll('.modal-container').forEach(consider);

  const roots = candidates.filter(
    (el) => !candidates.some((other) => other !== el && other.contains(el))
  );

  return roots.length;
}

export function shouldShowWhatsNewEpic4(opts: {
  proId: number | null | undefined;
  alreadySeen: boolean;
  pathname: string;
  accountLimited?: boolean;
  otherBlockingModals?: number;
}): boolean {
  if (!opts.proId) return false;
  if (opts.alreadySeen) return false;
  if (opts.accountLimited) return false;
  if (!isWhatsNewEpic4ConnectedPath(opts.pathname)) return false;
  if (isWhatsNewEpic4BlockedPath(opts.pathname)) return false;
  if ((opts.otherBlockingModals ?? 0) > 0) return false;
  return true;
}
